import { ReactNode } from 'react'
import Head from 'next/head'
import Link from 'next/link'

import ColoredLink from '../components/ColoredLink'
import IndentedList from '../components/IndentedList'
import Layout from '../components/Layout'

const questions = [
  {
    answer:
      'Jewish tradition is one of the most precious gifts you can give your newborn child. A bris, literally "covenant," symbolizes your desire to pass on this tradition to your child. It is not a medical procedure rather a visceral demonstration of your decision to bind your child to the Jewish people and a Jewish community.',
    question:
      'Can I or should I have a bris if I have not decided to raise my child as a Jew?',
  },
  {
    answer:
      'In Orthodox and Conservative Judaism, the baby follows the religion of the mother, which is called matrilineal descent. In Reform Judaism, the baby is considered Jewish if either parent is Jewish and both parents have agreed to raise the child as Jewish. That is, they accept patrilineal descent. Orthodox and Conservative communities would require conversion to follow the bris/baby naming in order for the child to fully enter the community. As a traditional Jew, I follow the matrilineal tradition and so will perform the bris only if the mother is Jewish or if the child undergoes conversion to Judaism.',
    question: 'What if the mother is not Jewish?',
  },
  {
    answer:
      'All branches of Judaism recognize the baby as Jewish. A traditional bris ceremony is held. However, since the father is not obligated to have a bris for his son, the mother recites the blessing after the circumcision. I encourage the father to fully participate in all other parts of the ceremony.',
    question: 'What if the father is not Jewish but the mother is Jewish?',
  },
  {
    answer:
      'Conversion for a baby involves three steps (two for girls). First, both parents must agree to raise the child Jewish. Second, the boy has a circumcision done with the appropriate blessings for conversion. Third, the baby is immersed in a Mikvah (ritual bath) when he/she is a few months old. (A Mikvah is a small, indoor pool filled with warm water. It sounds scary to immerse an infant, but babies tolerate it very well.) A Rabbi can provide more information on conversion and can supervise the conversion process.',
    question: 'What is involved in conversion?',
  },
  {
    answer:
      'The issue of "who is a Jew" generates a lot of emotion. If you raise your child without conversion, the Conservative and Orthodox movements will not recognize your child as being Jewish. This may cause problems later in life around issues such as summer camps, youth groups or marriage. To avoid these problems, many Reform families elect to convert the baby. Especially since conversion of an infant is very easy, with only the additional step of Mikvah. I strongly encourage you to discuss this with your Rabbi.',
    question:
      'If I am a Reform Jew married to a non-Jewish woman, why should I consider converting the baby?',
  },
  {
    answer:
      'A Rabbi needs to supervise each step of the conversion process. It is very important that you contact a Rabbi as soon as possible, ideally before the baby is born. The Rabbi will want to talk with you about this important decision to decide if it is appropriate. The Rabbi does not need to attend the bris but does need to agree to supervise the conversion before I can perform the bris.',
    question: 'Who supervises the conversion?',
  },
  {
    answer: (
      <span>
        Don't worry, Rabbis are used to getting calls like this from people they
        don't know. The birth of a baby is a chance to get to know a new family
        and potential congregant.{' '}
        <ColoredLink href="https://tcjewfolk.com/directory-page/">
          Here is a listing of all the synagogues in the Twin Cities' area.
        </ColoredLink>
      </span>
    ),
    question: "What if I don't have a Rabbi?",
  },
  {
    answer:
      'I make the ceremony feel very similar to a bris. The only difference is that, immediately before the actual circumcision, I say a blessing (in Hebrew) for conversion rather than the traditional blessing for a bris. We also skip a short verse that the parents say immediately after a bris.',
    question:
      'Does the ceremony for a bris preceding conversion differ from a regular bris?',
  },
  {
    answer: (
      <span>
        There are some traditional honors that must be assigned to Jews but
        there are several English readings that can be assigned to friends and
        relatives of other faith backgrounds. (
        <Link
          className="font-medium text-petrol underline decoration-gold underline-offset-4"
          href="/need"
          passHref={true}
        >
          See the What You Need section
        </Link>
        ). In addition, I explain each step of the ceremony, according to the
        needs of the group, so everyone knows what is going on.
      </span>
    ),
    question: 'Can my relatives of another faith be involved in the bris?',
  },
  {
    answer:
      'If the child is not circumcised, he would need to be, regardless of age. This is usually done in an operating room with general anesthesia except in infants. If the child was circumcised, but not for the sake of conversion, he would need a symbolic circumcision, which involves drawing a drop of blood from the top of the penis. There is only minor discomfort, but to avoid worry, it is best to make these decisions when the child is too young to remember.',
    question: 'Could we decide to convert the child when he is older?',
  },
] as const

export default function Interfaith(): JSX.Element {
  return (
    <Layout>
      <Head>
        <title>Robert Karasov - Minneapolis Mohel | Interfaith Couples</title>
      </Head>
      <h1 className="mb-6 font-serif text-4xl text-ink sm:text-3xl">
        Interfaith Couples
      </h1>
      <div className="space-y-6 leading-relaxed text-ink">
        <p>
          Having a baby brings much excitement and many challenges. If you are
          an interfaith couple, you may have some specific questions about a
          bris/baby naming ceremony or about raising a Jewish child. Here are
          some common questions and answers.
        </p>
        <IndentedList>
          {questions.map(({ question }, i) => {
            return (
              <li key={question} className="py-1">
                <ColoredLink href={`#q${i}`}>{question}</ColoredLink>
              </li>
            )
          })}
        </IndentedList>
        {questions.map(({ answer, question }, i) => {
          return (
            <Question key={question} id={`q${i}`} title={question}>
              {answer}
            </Question>
          )
        })}
      </div>
    </Layout>
  )
}

const Question: React.FC<{
  children: ReactNode
  id: string
  title: string
}> = ({ children, id, title }) => {
  return (
    <section>
      <h2 className="relative mb-3 mt-8 font-serif text-xl text-ink">
        {/* The ID is on this span so we can offset it on mobile to account for our fixed header. */}
        <span className="absolute top-0 sm:top-neg-small-header" id={id} />
        {title}
      </h2>
      <p>{children}</p>
    </section>
  )
}
